import React from 'react';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import TargetPicker, { NEW_PASS, NEW_FAIL, NEW_STEP } from './TargetPicker';
import { ArrowRight } from 'lucide-react';
import { nodeIds } from './graphModel';
import { AddButton, RemoveButton } from './formParts';

/**
 * Where the walk goes next, decided by this node's output.
 *
 *   [ field  op  value ]  →  node
 *   ...
 *   otherwise             →  node        (required; the walk always continues)
 *
 * Conditions on one field must not overlap and must name declared members /
 * values inside the declared range — the server validator enforces that and
 * its message lands on this node.
 *
 * A target can be an existing node, a brand-new pass or fail verdict, or a
 * new step inserted on that edge (the palette opens).
 */

const OPS_FOR = {
  boolean: [['eq', 'is'], ['neq', 'is not']],
  enum: [['eq', 'is'], ['neq', 'is not'], ['in', 'is one of'], ['not_in', 'is not one of']],
  number: [['gte', '≥'], ['gt', '>'], ['lte', '≤'], ['lt', '<'], ['eq', '='], ['neq', '≠'], ['between', 'between']],
};


const input = 'h-7 rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-base)] px-2 text-[12px] text-[color:var(--text-primary)] outline-none focus:border-[color:var(--border-strong)]';

function defaultCondition(output) {
  const [field, spec] = Object.entries(output || {})[0] || ['value', { type: 'boolean' }];
  if (spec.type === 'boolean') return { field, op: 'eq', value: true };
  if (spec.type === 'enum') return { field, op: 'eq', value: (spec.values || [])[0] ?? '' };
  return { field, op: 'gte', value: spec.min ?? 0 };
}

function ValueInput({ spec, when, onChange, readOnly, idx }) {
  const label = `Edge ${idx + 1} value`;
  if (!spec) return <input value={when.value ?? ''} disabled={readOnly} aria-label={label} onChange={(e) => onChange({ value: e.target.value })} className={`${input} min-w-0 flex-1`} />;
  if (spec.type === 'boolean') {
    return (
      <div className="w-24"><BrandDropdown compact ariaLabel={label} value={String(when.value)} disabled={readOnly} onChange={(v) => onChange({ value: v === 'true' })} options={[{ value: 'true', label: 'true' }, { value: 'false', label: 'false' }]} /></div>
    );
  }
  if (spec.type === 'enum') {
    const multi = when.op === 'in' || when.op === 'not_in';
    if (multi) {
      const sel = Array.isArray(when.value) ? when.value : [];
      return (
        <div className="flex min-w-0 flex-1 flex-wrap gap-1" aria-label={label}>
          {(spec.values || []).map((m) => (
            <label key={m} className={`inline-flex cursor-pointer items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] ${sel.includes(m) ? 'border-[color:var(--border-strong)] bg-surface-hover' : 'border-[color:var(--border-default)]'}`}>
              <input type="checkbox" className="hidden" disabled={readOnly} checked={sel.includes(m)}
                onChange={(e) => onChange({ value: e.target.checked ? [...sel, m] : sel.filter((x) => x !== m) })} />
              {m}
            </label>
          ))}
        </div>
      );
    }
    return (
      <div className="min-w-0 flex-1"><BrandDropdown compact ariaLabel={label} value={when.value ?? ''} disabled={readOnly} onChange={(v) => onChange({ value: v })} options={(spec.values || []).map((m) => ({ value: m, label: m, monospace: true }))} /></div>
    );
  }
  if (when.op === 'between') {
    // The server's shape is {min, max} (inclusive ends); an older draft may hold [lo, hi].
    const src = when.range || {};
    const r = Array.isArray(src) ? src : [src.min ?? spec.min ?? 0, src.max ?? spec.max ?? 1];
    const keep = Array.isArray(src) ? {} : src;
    return (
      <div className="flex min-w-0 flex-1 items-center gap-1">
        <input type="number" step="any" value={r[0]} disabled={readOnly} aria-label={`${label} low`} onChange={(e) => onChange({ range: { ...keep, min: Number(e.target.value), max: r[1] }, value: undefined })} className={`${input} w-20`} />
        <span className="text-[11px] text-[color:var(--text-muted)]">and</span>
        <input type="number" step="any" value={r[1]} disabled={readOnly} aria-label={`${label} high`} onChange={(e) => onChange({ range: { ...keep, min: r[0], max: Number(e.target.value) }, value: undefined })} className={`${input} w-20`} />
      </div>
    );
  }
  return (
    <input type="number" step="any" min={spec.min} max={spec.max} value={when.value ?? ''} disabled={readOnly} aria-label={label}
      onChange={(e) => onChange({ value: e.target.value === '' ? '' : Number(e.target.value), range: undefined })} className={`${input} w-24`} />
  );
}

export default function EdgesEditor({ graph, nodeId, onChange, onNewVerdict, onNewStep, readOnly = false }) {
  const node = graph.nodes[nodeId];
  const output = node?.output || {};
  const edges = node?.edges || [];
  const fieldNames = Object.keys(output);

  const targets = nodeIds(graph).filter((id) => id !== nodeId);

  const setEdge = (i, next) => onChange(edges.map((e, j) => (j === i ? next : e)));
  const setTarget = (i, choice) => {
    const e = edges[i];
    if (choice === NEW_PASS || choice === NEW_FAIL) { onNewVerdict(i, choice === NEW_PASS); return; }
    if (choice === NEW_STEP) { onNewStep(i); return; }
    setEdge(i, e.default !== undefined ? { default: choice } : { ...e, to: choice });
  };
  const addCondition = () => {
    const dflt = edges.findIndex((e) => e.default !== undefined);
    const target = dflt >= 0 ? edges[dflt].default : targets[0];
    const next = [...edges];
    next.splice(dflt >= 0 ? dflt : next.length, 0, { when: defaultCondition(output), to: target });
    onChange(next);
  };
  const remove = (i) => onChange(edges.filter((_, j) => j !== i));

  const TargetSelect = ({ i, value }) => (
    <TargetPicker graph={graph} nodeId={nodeId} value={value} disabled={readOnly} className="w-[40%] min-w-[120px]"
      ariaLabel={`Edge ${i + 1} target`} onChange={(v) => setTarget(i, v)} />
  );

  return (
    <div className="space-y-1.5">
      {edges.map((e, i) => {
        if (e.default !== undefined) {
          return (
            <div key={i} className="flex items-center gap-1.5 rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] p-1.5">
              <span className="w-[54%] pl-1 text-[12px] italic text-[color:var(--text-secondary)]">{edges.length > 1 ? 'otherwise' : 'always'}</span>
              <ArrowRight size={12} className="shrink-0 text-[color:var(--text-muted)]" />
              <TargetSelect i={i} value={e.default} />
            </div>
          );
        }
        const when = e.when || {};
        const spec = output[when.field];
        const ops = OPS_FOR[spec?.type] || OPS_FOR.boolean;
        return (
          <div key={i} className="rounded-md border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] p-1.5">
            <div className="flex items-center gap-1.5">
              <div className="w-[26%]">
                <BrandDropdown compact ariaLabel={`Edge ${i + 1} field`} value={when.field || ''} disabled={readOnly}
                  onChange={(f) => setEdge(i, { ...e, when: { ...defaultCondition({ [f]: output[f] }) } })} options={fieldNames.map((f) => ({ value: f, label: f, monospace: true }))} />
              </div>
              <div className="w-[24%]">
              <BrandDropdown compact ariaLabel={`Edge ${i + 1} operator`} value={when.op || ''} disabled={readOnly}
                onChange={(op) => {
                  const next = { ...when, op };
                  if (op === 'between') { next.range = { min: spec?.min ?? 0, max: spec?.max ?? 1 }; delete next.value; }
                  else if (when.range) { delete next.range; next.value = spec?.type === 'enum' ? (spec.values || [])[0] : spec?.min ?? 0; }
                  if ((op === 'in' || op === 'not_in') && !Array.isArray(next.value)) next.value = next.value != null && next.value !== '' ? [next.value] : [];
                  if (op !== 'in' && op !== 'not_in' && Array.isArray(next.value)) next.value = next.value[0] ?? (spec?.values || [])[0] ?? '';
                  setEdge(i, { ...e, when: next });
                }} options={ops.map(([op, label]) => ({ value: op, label }))} />
              </div>
              <ValueInput spec={spec} when={when} idx={i} readOnly={readOnly} onChange={(patch) => setEdge(i, { ...e, when: { ...when, ...patch } })} />
              {!readOnly && <RemoveButton label={`Remove edge ${i + 1}`} onClick={() => remove(i)} />}
            </div>
            <div className="mt-1 flex items-center gap-1.5 pl-1">
              <ArrowRight size={12} className="shrink-0 text-[color:var(--text-muted)]" />
              <span className="text-[11px] text-[color:var(--text-muted)]">then</span>
              <TargetSelect i={i} value={e.to} />
            </div>
          </div>
        );
      })}
      {!readOnly && fieldNames.length > 0 && (
        <AddButton onClick={addCondition}>Add condition</AddButton>
      )}
    </div>
  );
}
